import { TixDate } from '@tix/tix-time'

import type { CalendarDate, CalendarMonthData, DayData } from './types'

/**
 * Formats a day's price for display.
 * TODO: Currency symbol is hardcoded ($). Wrong for non-USD tenants.
 */
export function formatDayPrice(day: Pick<DayData, 'price' | 'pricePrefix'>): string {
  if (day.price === undefined) return ''
  if (day.price === 0) return 'Free'
  const amount = `$${day.price.toFixed(2)}`
  return day.pricePrefix ? `${day.pricePrefix} ${amount}` : amount
}

/**
 * Returns the availability label for a day when below threshold (e.g. "5 left"), or '' otherwise.
 */
export function getAvailabilityLabel(day: Pick<DayData, 'available'>, capacityThreshold: number | false): string {
  if (!capacityThreshold || day.available === undefined) return ''
  if (day.available < capacityThreshold) return `${day.available} left`
  return ''
}

/**
 * Pads a number to two digits with a leading zero if needed.
 */
function twoDigits(int: number): string {
  return String(int).padStart(2, '0')
}

/**
 * Finds the closest enabled date from the initial date in the given direction.
 * Used for keyboard navigation (arrow keys).
 *
 * @param dates - Index of DayData by date string (YYYY-MM-DD). Keys must be in chronological order.
 * @throws If initial is not within the date range of dates. Callers (e.g. CalendarPicker) must
 *   ensure the focused date is in range; CalendarPicker documents that index must not change after mount.
 */
export function findClosestEnabledDate(
  dates: Record<string, DayData>,
  initial: TixDate,
  offset: number,
): TixDate | undefined {
  const keys = Object.keys(dates)

  // Get the first and last date in the provided dates list.
  const start = new TixDate(keys[0])
  const end = new TixDate(keys[keys.length - 1])

  if (!initial.isBetween(start, end, undefined, '[]')) {
    throw new Error('Tix: findClosestEnabledDate(): Initial date was outside the range')
  }

  if (offset === 0) return undefined

  // Step through dates until an enabled date is found or the needle is out of range.
  // The offset determines the size and direction of each step.
  let result = initial
  do {
    result = result.add(offset, 'day')
    if (dates[result.date]?.enabled) {
      return result
    }
  } while (result.isBetween(start, end, undefined, '()'))

  return undefined
}

/**
 * Gets the month elements in the viewport and scrolls to previous/next one.
 * @param wrapper - The calendar picker wrapper element for this instance (avoids document.querySelector when multiple pickers exist).
 */
export function scrollButtonHandler(wrapper: HTMLElement, visibleMonthIDs: Set<string>, direction: number): void {
  function getMonthElement(id: string): HTMLElement | null {
    return wrapper.querySelector(`.calendar .picker-month[data-id="${id}"]`)
  }

  // Convert the set to an array and order the IDs in it.
  const keys = [...visibleMonthIDs].sort((a, b) => a.localeCompare(b))

  const lastMonthEl = getMonthElement(keys[keys.length - 1])
  const firstMonthEl = getMonthElement(keys[0])

  if (!lastMonthEl || !firstMonthEl) return

  const nextMonth = lastMonthEl.nextElementSibling
  const previousMonth = firstMonthEl.previousElementSibling
  const monthElement = direction > 0 ? nextMonth : previousMonth

  // There is no previous/next month when all available dates are in the same month.
  if (monthElement) {
    monthElement.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest',
    })
  }
}

/**
 * Creates an index of DayData by date string (YYYY-MM-DD).
 */
export function calendarDatesIndex(days: DayData[]): Record<string, DayData> {
  const result: Record<string, DayData> = {}
  for (const day of days) {
    result[day.date.date] = day
  }
  return result
}

/**
 * Generates month data structures from the dates index.
 * Only includes months that have at least one available date.
 */
export function getMonths(data: Record<string, DayData>): Record<string, CalendarMonthData> {
  function firstAvailable(dates: string[]): TixDate | undefined {
    const date = dates.find((date) => data[date].status === 'available')
    return date ? new TixDate(date) : undefined
  }

  const dates = Object.keys(data)
  if (dates.length === 0) return {}

  const first = firstAvailable(dates)
  const last = firstAvailable([...dates].reverse())

  if (!first || !last) return {}

  const result: Record<string, CalendarMonthData> = {}
  for (let i = first; i.isSameOrBefore(last, 'month'); i = i.add(1, 'month')) {
    const monthId = i.format('YYYY-MM')
    result[monthId] = {
      id: monthId,
      year: Number(i.format('YYYY')),
      month: Number(i.format('MM')),
      formatted: i.format('MMMM YYYY'),
      weeks: getWeeksInMonth(monthId, data),
    }
  }
  return result
}

/**
 * Generates the week grid for a single month.
 * @param monthId Formatted YYYY-MM
 * @param data The dates index
 */
function getWeeksInMonth(monthId: string, data: Record<string, DayData>): CalendarDate[][] {
  // Initialize a two-dimensional 6x7 array to represent a month of a calendar grid.
  //   - 6 rows/weeks. A month that starts on Saturday 1st and finishes on Monday 31st covers 6 calendar weeks.
  //   - 7 columns; days per week.
  // All values initialize to undefined.
  const result: CalendarDate[][] = [new Array(7), new Array(7), new Array(7), new Array(7), new Array(7), new Array(7)]

  const daysInMonth = new TixDate(`${monthId}-15`).daysInMonth()
  let week = 0

  // Iterate each day of the month by week-of-month (zero indexed) and weekday (Sunday is zero).
  for (let i = 1; i <= daysInMonth; i++) {
    const dayNumber = twoDigits(i)
    const date = new TixDate(`${monthId}-${dayNumber}`)
    const dayOfWeek = Number(date.format('d'))

    // Increment week index on Sundays, except the first day of the month.
    if (dayOfWeek === 0 && i > 1) week++

    // Specify the CalendarDate details for each day of the month.
    result[week][dayOfWeek] = {
      formatted: date.date,
      dayOfMonth: i,
      // Include the API DayData, if there is any.
      data: data[date.date],
    }
  }

  return result
}
